import type { DayStatus } from "@sundial/core";

interface IconProps {
  size?: number;
}

const SunIcon = ({ size = 24 }: IconProps) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = ({ size = 24 }: IconProps) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const HalfSunIcon = ({ size = 24 }: IconProps) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <path d="M17 17 Q20 14 17 11" strokeDasharray="2 2" />
  </svg>
);

const SickIcon = ({ size = 24 }: IconProps) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M8 15s1.5-2 4-2 4 2 4 2" />
    <line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="3" />
    <line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="3" />
  </svg>
);

const HolidayIcon = ({ size = 24 }: IconProps) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 18a5 5 0 0 0-10 0" />
    <line x1="12" y1="9" x2="12" y2="2" />
    <line x1="4.22" y1="10.22" x2="5.64" y2="11.64" />
    <line x1="1" y1="18" x2="3" y2="18" />
    <line x1="21" y1="18" x2="23" y2="18" />
    <line x1="18.36" y1="11.64" x2="19.78" y2="10.22" />
    <line x1="23" y1="22" x2="1" y2="22" />
    <polyline points="8 6 12 2 16 6" />
  </svg>
);

const ICON: Record<DayStatus, (p: IconProps) => JSX.Element> = {
  full: SunIcon,
  half: HalfSunIcon,
  none: MoonIcon,
  sick: SickIcon,
  holiday: HolidayIcon,
};

const LABEL: Record<DayStatus, string> = {
  full: "full",
  half: "half",
  none: "none",
  sick: "sick",
  holiday: "holiday",
};

export function SunTile(props: {
  date: string;
  status?: DayStatus;
  today?: boolean;
  hasNote?: boolean;
  /** Number of clients billed on this date; a badge shows when > 1. */
  clients?: number;
  /** Dimmed under a client filter (day not billed to that client) — still editable. */
  dimmed?: boolean;
  onClick?: () => void;
}) {
  const cls = ["tile", props.status ?? "empty", props.today ? "today" : ""].join(" ").trim();
  const label = props.status ? `${props.date} — ${LABEL[props.status]}` : props.date;

  const dayNum = Number(props.date.slice(8, 10));
  const IconComponent = props.status ? ICON[props.status] : null;
  // full = white on orange. sick/holiday = dark text on fixed pastel fills.
  // everything else follows the theme text colour (theme-aware tile bg).
  const fg =
    props.status === "full"
      ? "#fff"
      : props.status === "sick" || props.status === "holiday"
        ? "#1B2A4A"
        : "var(--text, #1B2A4A)";

  return (
    <button
      className={cls}
      aria-label={label}
      onClick={props.onClick}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: props.dimmed ? 0.35 : 1,
        transition: "opacity .15s",
      }}
    >
      {/* small status icon, top-left corner */}
      {IconComponent && (
        <span style={{ position: "absolute", top: "3px", left: "4px", display: "flex", color: fg, opacity: 0.8 }}>
          <IconComponent size={12} />
        </span>
      )}
      {/* day number — centered, the focal point */}
      <span style={{ fontSize: "15px", fontWeight: 600, lineHeight: 1, color: fg }}>{dayNum}</span>
      {/* note indicator dot */}
      {props.hasNote && (
        <span data-testid="note-dot" style={{ position: "absolute", bottom: "4px", right: "5px", width: "5px", height: "5px", borderRadius: "50%", background: fg }} />
      )}
      {/* multi-client badge — count in a chip, top-right (never colour-only) */}
      {(props.clients ?? 0) > 1 && (
        <span
          data-testid="multi-client-badge"
          aria-label={`${props.clients} clients`}
          style={{
            position: "absolute",
            top: "-5px",
            right: "-5px",
            minWidth: "17px",
            height: "17px",
            padding: "0 3px",
            borderRadius: "9px",
            background: "var(--card, #fff)",
            border: "2px solid var(--primary-deep, #D85F0E)",
            color: "var(--primary-deep, #D85F0E)",
            font: "700 10px 'JetBrains Mono', monospace",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {props.clients}
        </span>
      )}
    </button>
  );
}
